import { createApiRef } from '@backstage/core-plugin-api';
import { UnitCompletionRecord } from '../types';

/**
 * Current completion status of a `LearningContent` `unit` (Tip) entity for
 * the signed-in user.
 */
export interface UnitCompletionStatus {
  completed: boolean;
  /**
   * ISO timestamp the unit was marked completed, or `undefined` when
   * `completed` is `false`.
   */
  completedAt?: string;
}

/**
 * Client for `@platt/plugin-platt-onboarding-backend`'s unit (Tip)
 * completion API.
 *
 * @remarks
 * `getUnitCompletion`/`completeUnit`/`uncompleteUnit` are implicitly scoped
 * to the signed-in user: the backend derives the acting user from the
 * request's own credentials rather than from any parameter, so there is no
 * `userRef` to pass here. `listCompletions`, by contrast, returns every
 * user's completions for the given units, since it powers the team-wide
 * progress matrices.
 */
export interface OnboardingApi {
  /**
   * Returns whether the signed-in user has completed the given unit.
   */
  getUnitCompletion(unitRef: string): Promise<UnitCompletionStatus>;

  /**
   * Marks the given unit as completed by the signed-in user.
   */
  completeUnit(unitRef: string): Promise<UnitCompletionStatus>;

  /**
   * Clears a previously recorded completion for the given unit.
   */
  uncompleteUnit(unitRef: string): Promise<UnitCompletionStatus>;

  /**
   * Returns every recorded completion of the given units, across all users.
   * Returns an empty array without calling the backend when `unitRefs` is
   * empty.
   */
  listCompletions(unitRefs: string[]): Promise<UnitCompletionRecord[]>;
}

/** @public */
export const onboardingApiRef = createApiRef<OnboardingApi>({
  id: 'plugin.platt-onboarding.service',
});
